import { z } from 'zod';
import * as C from '@bs/contracts';

/**
 * Typed view of @bs/contracts `routes`.
 *
 * Why this exists: contracts' `r()` helper returns `RouteDef`, which widens every schema to
 * `ZodTypeAny`, so `routes.login.body` has no static type. Until contracts changes `r` to
 * `<T extends RouteDef>(d: T) => T`, this file re-attaches the exact schemas. Method and path always
 * come from `routes` at runtime; test/typedRoutes.test.ts checks every body/query here is the SAME
 * schema object as in `routes`, so the two cannot drift silently.
 */
const list = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item) });
const def = <B extends z.ZodTypeAny | undefined, Q extends z.ZodTypeAny | undefined, R extends z.ZodTypeAny>(d: { body?: B; query?: Q; response: R }) => d as { body: B; query: Q; response: R };

export const typed = {
  login: def({ body: C.LoginRequest, response: C.LoginResponse }),
  acceptInvite: def({ body: C.AcceptInviteRequest, response: C.OkResponse }),
  logout: def({ response: C.OkResponse }),
  me: def({ response: C.Me }),
  listVerticals: def({ response: list(C.Vertical) }),
  getVertical: def({ response: C.Vertical }),
  listUsers: def({ response: list(C.User) }),
  inviteUser: def({ body: C.InviteUserRequest, response: C.InviteUserResponse }),
  setMemberships: def({ body: C.SetMembershipsRequest, response: C.User }),
  listContacts: def({ query: C.ContactListQuery, response: C.page(C.Contact) }),
  dedupeContacts: def({ query: C.DedupeQuery, response: C.DedupeResponse }),
  createContact: def({ body: C.CreateContactRequest, response: C.Contact }),
  getContact: def({ response: C.Contact }),
  updateContact: def({ body: C.UpdateContactRequest, response: C.Contact }),
  listAccounts: def({ query: C.AccountListQuery, response: C.page(C.Account) }),
  createAccount: def({ body: C.CreateAccountRequest, response: C.Account }),
  getAccount: def({ response: C.Account }),
  updateAccount: def({ body: C.UpdateAccountRequest, response: C.Account }),
  listRelationships: def({ query: C.RelationshipListQuery, response: C.page(C.Relationship) }),
  createRelationship: def({ body: C.CreateRelationshipRequest, response: C.Relationship }),
  getRelationship: def({ response: C.Relationship }),
  updateRelationship: def({ body: C.UpdateRelationshipRequest, response: C.Relationship }),
  reassignRelationship: def({ body: C.ReassignRequest, response: C.ReassignResponse }),
  assignmentHistory: def({ response: list(C.AssignmentHistoryItem) }),
  listLog: def({ query: C.LogListQuery, response: C.page(C.LogEntry) }),
  createLog: def({ body: C.CreateLogEntryRequest, response: C.LogEntry }),
  listCommitments: def({ response: list(C.Commitment) }),
  createCommitment: def({ body: C.CreateCommitmentRequest, response: C.Commitment }),
  updateCommitment: def({ body: C.UpdateCommitmentRequest, response: C.Commitment }),
  listDrafts: def({ query: C.DraftListQuery, response: C.page(C.Draft) }),
  createDraft: def({ body: C.CreateDraftRequest, response: C.Draft }),
  updateDraft: def({ body: C.UpdateDraftRequest, response: C.Draft }),
  markSent: def({ body: C.MarkSentRequest, response: C.MarkSentResponse }),
  listMemory: def({ query: C.MemoryListQuery, response: C.page(C.MemoryItem) }),
  deleteMemory: def({ response: z.undefined() }),
  presignUpload: def({ body: C.PresignUploadRequest, response: C.PresignUploadResponse }),
  confirmUpload: def({ response: C.FileObject }),
  listFiles: def({ response: list(C.FileObject) }),
  presignDownload: def({ response: C.PresignDownloadResponse }),
  listNotifications: def({ query: C.NotificationListQuery, response: C.page(C.Notification) }),
  markNotificationRead: def({ response: C.Notification }),
  today: def({ query: C.TodayQuery, response: C.TodayBrief }),
} satisfies Record<keyof typeof C.routes, { body: unknown; query: unknown; response: z.ZodTypeAny }>;
